import {FormattedMessage, useIntl} from "react-intl";
import React, {useContext, useEffect, useMemo, useRef, useState} from "react";
import '../scss/SurveyHeader.scss';
import {Link, Prompt, useHistory, useLocation, useParams} from "react-router-dom";
import { Loader2 } from "lucide-react";
import {ReactComponent as BackArrow} from "../svg/back.svg";
import {Modal, Popup, PopupElement, PopupProvider, StatusText, Toast, usePopup} from "../UIElements";
import {
    EditorFocusContext,
    useOriginalSurveyData,
    useOriginalUserSurveyData,
    useSurveyData,
    useUserSurveyData
} from "./SurveyView";
import {
    GetSurveyWithId,
    GetUserBySurvey,
    PostSurveyWithId,
    PostUsersToAnnieUserSurvey,
    UpdateSurveyWithId, UpdateSurveyWithIdAndUpdateContext
} from "../api/APISurvey";
import {replaceInstructionVariables, updateTimeToServerFormat} from "../Formats";
import _, {isPlainObject} from "lodash";
import {ReactComponent as RedoIcon} from "../svg/redo.svg";
import {ReactComponent as UndoIcon} from "../svg/undo.svg";
import {ReactComponent as TestIcon} from "../svg/test.svg";
import {useAuthData, useCurrentUserData, useLanguageData} from "../App";
import {SurveySummary} from "./SurveySummary";
import {useSnackbar} from "../Content";
import {CopyPopup} from "./Surveys";
import {useDataContext, useUndoRedo} from "./SurveyContextProvider";
import {
    reconstructConfigObject,
} from "../DataFunctions";
import {TestSurveyModal} from "./TestSurvey";
import {useSurveySave} from "./surveyEditor/hooks/useSurveySave";

function SurveyOverviewModal({ isOpen, onClose, onConfirm, confirmText, isLoading, isSaving = null, published = false, showSkipChecksButton = false}) {
    const [surveyChecked, setSurveyChecked] = useState(false);
    const intl = useIntl();
    const { currentUserData } = useCurrentUserData();
    const userSuperAdmin = currentUserData.superuser && currentUserData?.meta?.email.endsWith("@annieadvisor.com");

    if (!isOpen) return null;

    const savingText = intl.formatMessage({
        id: 'saving',
        defaultMessage: 'Saving...'
    })

    const publishingText = intl.formatMessage({
        id: 'publishing',
        defaultMessage: 'Publishing...'
    })

    const getConfirmText = () => {
        if (isSaving === 'DRAFT' || published) {
            return (
                <>
                    <Loader2 size={16} className="spinner" />
                    {savingText}
                </>
            );
        } else if (isSaving === 'ACTIVE') {
            return (
                <>
                    <Loader2 size={16} className="spinner" />
                    {publishingText}
                </>
            );
        }
        return confirmText;
    };

    return (
        <Modal
            closeModal={onClose}
            header={intl.formatMessage({
                id: 'survey.overview',
                defaultMessage: 'Survey Overview',
            })}
            confirmAction={onConfirm}
            confirmText={getConfirmText()}
            scrollContent={true}
            confirmDisabled={!surveyChecked || isLoading}
            disabledText={isLoading ? "" :
                <>
                    {intl.formatMessage({
                        id: 'survey.overview.checkErrors',
                        defaultMessage: 'Check errors before publishing'
                    })}
                    {showSkipChecksButton && userSuperAdmin &&
                        <button className={"override"} onClick={() => setSurveyChecked(true)}>
                            {intl.formatMessage({
                                id: 'survey.overview.bypassTests',
                                defaultMessage: 'Skip checks',
                            })}
                        </button>
                    }
                </>
            }
        >
            <SurveySummary setSurveyChecked={surveyStatus => setSurveyChecked(surveyStatus)}/>
        </Modal>
    );
}

export default function SurveyHeader() {
    const { value: surveyData } = useDataContext('surveyData');
    const { value: userSurveyData } = useDataContext('userSurveyData');
    const { value: originalSurveyData } = useDataContext('originalSurveyData');
    const { value: originalUserSurveyData } = useDataContext('originalUserSurveyData');

    return (
        <PopupProvider>
            <HeaderRender
                surveyData={surveyData}
                originalSurveyData={originalSurveyData}
                userSurveyData={userSurveyData}
                originalUserSurveyData={originalUserSurveyData}
            />
            <PopupElement />
        </PopupProvider>
    );
}

function HeaderRender({surveyData, originalSurveyData, userSurveyData, originalUserSurveyData}) {
    const intl = useIntl();
    const history = useHistory();
    const { saveSurvey, clearSessionStorage } = useSurveySave();

    const [confirmNavigation, setConfirmNavigation] = useState(false);
    const [nextLocationPathname, setNextLocationPathname] = useState("");
    const [newSurveyPathname, setNewSurveyPathname] = useState("")
    const [navigationState, setNavigationState] = useState(null)

    // Effect to handle navigation after confirmation
    useEffect(() => {
        if (confirmNavigation && (newSurveyPathname || nextLocationPathname)) {
            clearSessionStorage();
            if (isPlainObject(navigationState)) {
                history.push({
                    pathname: newSurveyPathname || nextLocationPathname,
                    state: navigationState
                })
            } else {
                history.push(newSurveyPathname || nextLocationPathname);
            }
        }

        return () => {
            if (confirmNavigation) {
                setConfirmNavigation(false);
                setNavigationState(null)
                setNextLocationPathname("")
                setNewSurveyPathname("")
            }
        };
    }, [confirmNavigation, history, nextLocationPathname, navigationState, clearSessionStorage]);

    const surveyName = surveyData.config.title;
    let name;
    if (surveyData.id === "new" && !surveyName) {
        name = intl.formatMessage({
            id: 'survey.header.newSurvey',
            defaultMessage: 'New Survey',
        });
    } else {
        name = surveyName;
    }

    let unsavedChanges = JSON.stringify(surveyData) !== JSON.stringify(originalSurveyData) ||
        JSON.stringify(userSurveyData) !== JSON.stringify(originalUserSurveyData);

    return (
        <>
            <div className={"survey-header"}>
                <Link to="/surveys" className={"back"}>
                    <BackArrow />
                </Link>
                <h3>{name}</h3>
                <StatusText survey={surveyData}/>
                {unsavedChanges &&
                    <p className={"unsaved-changes"}>
                        {intl.formatMessage({
                            id: 'survey.header.unsavedChanges',
                            defaultMessage: 'Unsaved changes',
                        })}
                    </p>
                }
                <SaveEdits
                    setConfirmNavigation={setConfirmNavigation}
                    setNewSurveyPathname={setNewSurveyPathname}
                    setNavigationState={setNavigationState}
                />
                <PromptDialog
                    unsavedChanges={unsavedChanges}
                    confirmNavigation={confirmNavigation}
                    setConfirmNavigation={setConfirmNavigation}
                    setNewSurveyPathname={setNewSurveyPathname}
                    setNextLocationPathname={setNextLocationPathname}
                />
            </div>
        </>
    );
}

function PromptDialog({unsavedChanges, confirmNavigation, setConfirmNavigation, setNextLocationPathname }) {
    const { value: surveyData } = useDataContext('surveyData');
    const { value: newerSurveyDataAvailable } = useDataContext('newerSurveyDataAvailable');
    const intl = useIntl();
    const { saveSurvey, isLoading, isSaving } = useSurveySave();

    const [showPublishSummary, setShowPublishSummary] = useState(false);
    const [showSavePrompt, setShowSavePrompt] = useState(false)

    const surveyIsDraft = surveyData.hasOwnProperty("status") && surveyData.status === "DRAFT";
    const surveyIsDeleted = surveyData.hasOwnProperty("status") && surveyData.status === "DELETED";

    // Check if we should prompt before navigation
    const checkForNextLocation = (nextLocation) => {
        setNextLocationPathname(nextLocation.pathname);
        if (confirmNavigation) {
            return true;
        } else if (nextLocation.pathname.includes(`/support-campaign/${surveyData?.id}`)) {
            return true
        } else {
            setShowSavePrompt(true);
            return false
        }
    };

    // Handle save or open overview based on survey status
    const saveOrOpenOverview = () => {
        if (surveyIsDraft || surveyIsDeleted) {
            saveSurvey(surveyData.id === "new", true, () => {
                setConfirmNavigation(true)
            }, 'DRAFT');
        } else {
            setShowSavePrompt(false);
            setShowPublishSummary(true);
        }
    };

    const handlePublish = () => {
        saveSurvey(surveyData.id === "new", false, () => {
            setConfirmNavigation(true);
        }, 'ACTIVE');
    }

    // Determine appropriate button text
    const modalSaveText = intl.formatMessage({
        id: 'survey.save.save',
        defaultMessage: 'Save',
    });

    const checkAndSave = intl.formatMessage({
        id: 'survey.save.checkSave',
        defaultMessage: 'Check & Save',
    });

    const savingText = intl.formatMessage({
        id: 'saving',
        defaultMessage: 'Saving...'
    })

    return (
        <>
            <Prompt
                when={unsavedChanges && !newerSurveyDataAvailable}
                message={checkForNextLocation}
            />

            {showSavePrompt && (
                <Modal
                    header={intl.formatMessage({
                        id: 'survey.unsavedChanges.header',
                        defaultMessage: 'Save changes to survey?',
                    })}
                    text={
                        <FormattedMessage
                            id={"survey.unsavedChanges.text"}
                            defaultMessage={"Do you wish to save changes before leaving page?{br}Any unsaved changes will be lost."}
                            values={{br: <br/>}}
                        />
                    }
                    discardText={intl.formatMessage({
                        id: 'survey.unsavedChanges.discard',
                        defaultMessage: "Don't save",
                    })}
                    confirmText={(surveyIsDraft || surveyIsDeleted) ? (
                        isSaving === 'DRAFT' ? (
                            <>
                                <Loader2 size={16} className="spinner" />
                                {savingText}
                            </>
                        ) : modalSaveText
                    ) : checkAndSave}
                    confirmDisabled={isLoading}
                    discardAction={() => setConfirmNavigation(true)}
                    closeModal={() => setShowSavePrompt(false)}
                    confirmAction={saveOrOpenOverview}
                />
            )}

            <SurveyOverviewModal
                isOpen={showPublishSummary}
                onClose={() => setShowPublishSummary(false)}
                isLoading={isLoading}
                isSaving={isSaving}
                onConfirm={handlePublish}
                confirmText={modalSaveText}
                showSkipChecksButton={true}
            />
        </>
    );
}

function SaveEdits({setConfirmNavigation, setNavigationState, setNewSurveyPathname}) {
    const { value: surveyData } = useDataContext('surveyData');
    const { value: userSurveyData } = useDataContext('userSurveyData');
    const intl = useIntl();
    const history = useHistory();
    const { surveyId } = useParams();
    const { saveSurvey, clearSessionStorage, isLoading, isSaving } = useSurveySave();
    const { currentUserData } = useCurrentUserData();
    const { showPopup, hidePopup } = usePopup();

    const [showPublishSummary, setShowPublishSummary] = useState(false);
    const [showDuplicatePopup, setShowDuplicatePopup] = useState(false);

    const createNewSurvey = surveyId === "new";
    const surveyIsDraft = surveyData.hasOwnProperty("status") && surveyData.status === "DRAFT";
    const surveyIsScheduled = surveyData.hasOwnProperty("status") && surveyData.status === "SCHEDULED";
    const surveyIsFinished = surveyData.hasOwnProperty("status") && surveyData.status === "FINISHED";
    const surveyIsDeleted = surveyData.hasOwnProperty("status") && surveyData.status === "DELETED";
    const userSuperAdmin = currentUserData.superuser && currentUserData?.meta?.email.endsWith("@annieadvisor.com");

    // Handle location state for displaying toasts
    let location = useLocation();
    const { setSnackbarData } = useSnackbar();

    useEffect(() => {
        if (location.state?.newSavedSurvey) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'toast.saveSuccess',
                    defaultMessage: 'Survey Saved',
                }),
                state: "success",
                autoHide: true,
            });
        }

        if (location.state?.surveyDuplicated) {
            setSnackbarData({
                show: true,
                text: intl.formatMessage({
                    id: 'toast.surveyDuplicated',
                    defaultMessage: 'Survey duplicated',
                }),
                state: "success",
                autoHide: true,
            });
        }
    }, [location.state, setSnackbarData, intl]);

    // Determine button text based on survey state
    let draftText = surveyIsDraft ?
        intl.formatMessage({
            id: 'survey.save.save',
            defaultMessage: 'Save',
        }) :
        intl.formatMessage({
            id: 'survey.save.draft',
            defaultMessage: 'Change to draft',
        });

    let saveText = (surveyIsDraft || createNewSurvey) ?
        intl.formatMessage({
            id: 'survey.save.checkPublish',
            defaultMessage: 'Check & Publish',
        }) :
        intl.formatMessage({
            id: 'survey.save.checkSave',
            defaultMessage: 'Check & Save',
        });

    let modalSaveText = (surveyIsDraft || createNewSurvey) ?
        intl.formatMessage({
            id: 'survey.save.publish',
            defaultMessage: 'Publish',
        }) :
        intl.formatMessage({
            id: 'survey.save.save',
            defaultMessage: 'Save',
        });

    // Navigate to newly created survey
    const navigateToNewIdAfterDuplication = (idForNavi) => {
        if (idForNavi) {
            setNavigationState({surveyDuplicated: true})
            setConfirmNavigation(true)
            setNewSurveyPathname(`/support-campaign/${idForNavi}`)
        }
    };

    const handleSave = () => {
        if (createNewSurvey) {
            saveSurvey(true, true, (newID) => {
                if (newID) {
                    setNavigationState({newSavedSurvey: true})
                    setConfirmNavigation(true)
                    setNewSurveyPathname(`/support-campaign/${newID}`)
                }
            }, 'DRAFT')
        } else {
            saveSurvey(false, true, null, 'DRAFT')
        }
    }

    const handlePublish = () => {
        if (createNewSurvey) {
            saveSurvey(true, false, (newID) => {
                if (newID) {
                    setNavigationState({newSavedSurvey: true})
                    setConfirmNavigation(true)
                    setNewSurveyPathname(`/support-campaign/${newID}`)
                }
            }, 'ACTIVE')
        } else {
            saveSurvey(false, false, () => {
                setShowPublishSummary(false)
            }, 'ACTIVE')
        }
    }

    return (
        <>
            <div className={"save-toolbar"}>
                <UndoRedoComponent />
                <button
                    className={"test-survey"}
                    onClick={() => showPopup(<TestSurveyModal onClose={() => hidePopup()}/>)}
                >
                    <TestIcon />
                    {intl.formatMessage({
                        defaultMessage: 'Send a Test',
                        id: 'sendATestSurvey'
                    })}
                </button>

                {(() => {
                    const isAnySaving = isSaving !== null;

                    return (
                        <>
                            {(surveyIsScheduled || surveyIsDraft || surveyIsDeleted) && (
                                <button
                                    className={`draft ${isAnySaving ? 'disabled' : ''}`}
                                    onClick={handleSave}
                                    disabled={isAnySaving}
                                >
                                    {isSaving === 'DRAFT' ? (
                                        <>
                                            <Loader2 size={16} className="spinner" />
                                            {intl.formatMessage({
                                                defaultMessage: 'Saving...',
                                                id: 'saving'
                                            })}
                                        </>
                                    ) : (
                                        draftText
                                    )}
                                </button>
                            )}
                            {(surveyIsScheduled || surveyIsFinished) && (
                                <button
                                    className={`draft ${isAnySaving ? 'disabled' : ''}`}
                                    onClick={() => setShowDuplicatePopup(true)}
                                    disabled={isAnySaving}
                                >
                                    {intl.formatMessage({
                                        id: "survey.duplicate",
                                        defaultMessage: "Duplicate"
                                    })}
                                </button>
                            )}
                            {!surveyIsDeleted && (
                                <button
                                    className={`save ${isAnySaving ? 'disabled' : ''}`}
                                    onClick={() => setShowPublishSummary(true)}
                                    disabled={isAnySaving}
                                >
                                    {isSaving === 'ACTIVE' ? (
                                        <>
                                            <Loader2 size={16} className="spinner" />
                                            {intl.formatMessage({
                                                defaultMessage: 'Publishing...',
                                                id: 'publishing'
                                            })}
                                        </>
                                    ) : (
                                        saveText
                                    )}
                                </button>
                            )}
                        </>
                    );
                })()}
            </div>

            <SurveyOverviewModal
                isOpen={showPublishSummary}
                onClose={() => setShowPublishSummary(false)}
                isLoading={isLoading}
                isSaving={isSaving}
                onConfirm={handlePublish}
                confirmText={modalSaveText}
                showSkipChecksButton={true}
            />

            {showDuplicatePopup && (
                <CopyPopup
                    survey={reconstructConfigObject(surveyData)}
                    closePopup={() => setShowDuplicatePopup(false)}
                    refetch={() => console.log("Duplicate created")}
                    duplicateFromSurveyEditor={true}
                    setNewIdForNavigation={navigateToNewIdAfterDuplication}
                />
            )}
        </>
    );
}

function UndoRedoComponent() {
    const intl = useIntl();
    const { anyEditorFocused } = useContext(EditorFocusContext);
    const { undo, redo, canUndo, canRedo } = useUndoRedo();

    const undoRedoRef = useRef({ undo, redo, canUndo, canRedo });
    undoRedoRef.current = { undo, redo, canUndo, canRedo };

    const handleKeyDown = (event) => {
        if (anyEditorFocused) return;

        if ((event.metaKey || event.ctrlKey) && event.key === 'z') {
            event.preventDefault();
            if (event.shiftKey) {
                if (undoRedoRef.current.canRedo) {
                    undoRedoRef.current.redo();
                }
            } else {
                if (undoRedoRef.current.canUndo) {
                    undoRedoRef.current.undo();
                }
            }
        }
        else if (event.ctrlKey && event.key === 'y') {
            event.preventDefault();
            if (undoRedoRef.current.canRedo) {
                undoRedoRef.current.redo();
            }
        }
    };

    useEffect(() => {
        document.addEventListener('keydown', handleKeyDown);
        return () => {
            document.removeEventListener('keydown', handleKeyDown);
        };
    }, [anyEditorFocused]);

    const undoText = intl.formatMessage({id:'undo', defaultMessage:'Undo'});
    const redoText = intl.formatMessage({id:'redo', defaultMessage:'Redo'});

    return (
        <div className={"undo-redo"}>
            {canUndo ? (
                <button title={undoText} onClick={undo}><UndoIcon /></button>
            ) : (
                <button disabled title={undoText}><UndoIcon /></button>
            )}
            {canRedo ? (
                <button title={redoText} onClick={redo}><RedoIcon /></button>
            ) : (
                <button title={redoText} disabled><RedoIcon /></button>
            )}
        </div>
    );
}